import React, { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import dayjs from 'dayjs';

import { ProductPurchaseServiceContext } from 'src/contexts/state-machines/purchase/ProductPurchaseServiceContext';
import { useIsMobile } from 'src/reservation-page/hooks/helpers/useIsMobile';
import { RSDetailHeader } from 'src/reservation-page/detail-header/RSDetailHeader';
import { RSDetailHeaderButton } from 'src/reservation-page/detail-header/RSDetailHeaderButton';
import { RSDescriptionList } from 'src/reservation-page/description-list/RSDescriptionList';
import { RSDescriptionListItem } from 'src/reservation-page/description-list/RSDescriptionListItem';
import { RSAnchor } from 'src/reservation-page/RSAnchor';
import { RSButton } from 'src/reservation-page/RSButton';
import {
  GMO_PG_PAYMENT_POPUP_WINDOW_NAME,
  openGmoPgPaymentPopupWindow,
} from 'src/utils/payment';

import styles from '../../index.module.css';
import { buildSiteUrl, buildUrl } from 'src/utils/request-url';

export interface ConfirmViewProps {}

export const ConfirmView: FC<ConfirmViewProps> = () => {
  const intl = useIntl();

  const isMobile = useIsMobile();

  const [state, send] = ProductPurchaseServiceContext.useActor();
  const dataProductDetail = ProductPurchaseServiceContext.useSelector(
    (state) => state.context.data.productDetail!,
  );
  const productPublicId = ProductPurchaseServiceContext.useSelector(
    (state) => state.context.data.productPublicId!,
  );
  const isMulpayOrderEnabled = ProductPurchaseServiceContext.useSelector(
    (state) => state.context.data.isMulpayOrderEnabled,
  );

  const onBackButtonClick = useCallback(() => {
    send({
      type: 'BACK_FROM_CONFIRMING_TO_MAIN',
    });
  }, [send]);

  const onSubmitButtonClick = useCallback(() => {
    // GMO-PG 決済の場合はリダイレクトが発生し、同じwindowで処理すると入力中の予約情報が失われてしまうため
    // ポップアップ(全画面であれば別タブ) を開きそこで処理する
    // ユーザーの操作後、ポップアップを開くまでに API 通信等の非同期処理等が挟まっていると、ポップアップブロックの対象になりえるため、
    // ユーザー操作直後のこのタイミング popup を開き、 state に保持しておく
    if (isMulpayOrderEnabled) {
      const productPurchaseStartUrl = buildSiteUrl(
        `/products/${productPublicId}/purchase/start`,
        'next',
      );
      const absoluteStartUrl = buildUrl(productPurchaseStartUrl, 'absolute');

      const popup = openGmoPgPaymentPopupWindow(
        absoluteStartUrl,
        GMO_PG_PAYMENT_POPUP_WINDOW_NAME,
      );
      if (!popup) throw new Error('Could not open a popup');

      send({
        type: 'SET_MULPAY_POPUP_WINDOW',
        payload: { popupWindow: popup ?? null },
      });
    }

    send({
      type: 'SUBSCRIPTION_SUBMIT',
    });
  }, [send, productPublicId, isMulpayOrderEnabled]);

  return (
    <>
      <div className="z-5 sticky top-0">
        <RSDetailHeader
          title={intl.formatMessage({
            defaultMessage: '月謝購入',
            id: 'qFdB5v',
            description:
              'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
          })}
          leftButton={
            <RSDetailHeaderButton variant="close" onClick={onBackButtonClick}>
              {intl.formatMessage({
                defaultMessage: '戻る',
                id: '3e9jze',
                description:
                  'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
              })}
            </RSDetailHeaderButton>
          }
        />
      </div>

      <div
        className={classnames(
          'overflow-y-auto',
          isMobile
            ? styles.mobileHeightRestriction
            : styles.pcHeightRestriction,
        )}
      >
        <div className="pc:px-4 pc:pb-4 pc:pt-4 px-2.5 pb-6 pt-3">
          <div className="mb-3">
            <div className="rs-font text-24 text-gray-1000 pc:text-28 font-bold">
              {intl.formatMessage({
                defaultMessage: 'この内容で購入しますか？',
                id: '3RuBip',
                description:
                  'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
              })}
            </div>
          </div>

          <div className="mb-4">
            <div className="flex items-center">
              {dataProductDetail.image_url && (
                <div className="mr-1.5 flex-none">
                  <img
                    className="box-border block h-[56px] rounded-[6px]"
                    src={dataProductDetail.image_url}
                    alt=""
                  />
                </div>
              )}

              <div className="flex-auto">
                <div className="rs-font text-16 text-gray-1000 pc:text-15 font-bold">
                  {dataProductDetail.name}
                </div>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <RSDescriptionList>
              <RSDescriptionListItem
                term={intl.formatMessage({
                  defaultMessage: '月額',
                  id: 'Et5+vL',
                  description:
                    'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
                })}
                description={
                  <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                    {intl.formatMessage(
                      {
                        defaultMessage: '{priceString}（税込）',
                        id: 'NKh1L0',
                        description:
                          'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
                      },
                      {
                        priceString: intl.formatNumber(
                          dataProductDetail.price,
                          {
                            style: 'currency',
                            currency: 'JPY',
                          },
                        ),
                      },
                    )}
                  </div>
                }
              />
              {(() => {
                if (dataProductDetail.is_wait_first_anchor) {
                  return (
                    <RSDescriptionListItem
                      term={intl.formatMessage({
                        defaultMessage: '支払日',
                        id: 'pFIGbV',
                        description:
                          'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
                      })}
                      description={
                        <>
                          <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                            {intl.formatMessage({
                              defaultMessage: '毎月1日',
                              id: 'ffYiSb',
                              description:
                                'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
                            })}
                          </div>
                          <div className="rs-font text-13 text-gray-1000 pc:text-12 font-normal">
                            {intl.formatMessage({
                              defaultMessage: '翌月からのお支払いになります',
                              id: 'SqfOon',
                              description:
                                'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
                            })}
                          </div>
                        </>
                      }
                    />
                  );
                }

                return (
                  <>
                    {(() => {
                      if (!dataProductDetail.is_first_charge_pro_rata_basis) {
                        return null;
                      }

                      return (
                        <RSDescriptionListItem
                          term={intl.formatMessage({
                            defaultMessage: '当月分の支払額',
                            id: 'ojvRDf',
                            description:
                              'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
                          })}
                          description={
                            <>
                              <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                                {intl.formatMessage(
                                  {
                                    defaultMessage: '{priceString}（税込）',
                                    id: 'NKh1L0',
                                    description:
                                      'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
                                  },
                                  {
                                    priceString: intl.formatNumber(
                                      dataProductDetail.amount_user_will_pay,
                                      {
                                        style: 'currency',
                                        currency: 'JPY',
                                      },
                                    ),
                                  },
                                )}
                              </div>
                              <div className="rs-font text-13 text-gray-1000 pc:text-12 font-normal">
                                {intl.formatMessage({
                                  defaultMessage: '月額から日割りした金額です',
                                  id: 'g2HRQV',
                                  description:
                                    'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
                                })}
                              </div>
                            </>
                          }
                        />
                      );
                    })()}
                    <RSDescriptionListItem
                      term={intl.formatMessage({
                        defaultMessage: '当月分の支払日',
                        id: 'M8CQ3T',
                        description:
                          'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
                      })}
                      description={
                        <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                          {intl.formatDate(dayjs().toDate(), {
                            weekday: 'narrow',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </div>
                      }
                    />
                    <RSDescriptionListItem
                      term={intl.formatMessage({
                        defaultMessage: '翌月以降の支払日',
                        id: 'DRZBDd',
                        description:
                          'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
                      })}
                      description={
                        <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                          {intl.formatMessage({
                            defaultMessage: '毎月1日',
                            id: 'ffYiSb',
                            description:
                              'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
                          })}
                        </div>
                      }
                    />
                  </>
                );
              })()}
              {dataProductDetail.available_months_period && (
                <RSDescriptionListItem
                  term={intl.formatMessage({
                    defaultMessage: '有効期限',
                    id: 'Jj4ISk',
                    description:
                      'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
                  })}
                  description={
                    <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                      {dayjs()
                        .tz('Asia/Tokyo')
                        .add(dataProductDetail.available_months_period, 'month')
                        .endOf('month')
                        .format('YYYY年M月D日')}
                    </div>
                  }
                />
              )}
              {(() => {
                if (
                  Object.values(
                    dataProductDetail.limit_reservation_count,
                  ).every((value) => value === null)
                ) {
                  return null;
                }

                const { monthly, weekly, daily, upcoming } =
                  dataProductDetail.limit_reservation_count;

                return (
                  <RSDescriptionListItem
                    // term="回数制限"
                    term={intl.formatMessage({
                      defaultMessage: '回数制限',
                      id: 'uDV3/3',
                      description:
                        'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
                    })}
                    description={
                      <>
                        {monthly !== null && (
                          <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                            {intl.formatMessage(
                              {
                                defaultMessage: 'ひと月当たり {monthly}回',
                                id: 'GIfErS',
                                description:
                                  'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
                              },
                              { monthly },
                            )}
                          </div>
                        )}
                        {weekly !== null && (
                          <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                            {intl.formatMessage(
                              {
                                defaultMessage: '１週間当たり {weekly}回',
                                id: 'pUXlPY',
                                description:
                                  'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
                              },
                              {
                                weekly,
                              },
                            )}
                          </div>
                        )}
                        {daily !== null && (
                          <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                            {intl.formatMessage(
                              {
                                defaultMessage: '１日当たり {daily}回',
                                id: 'AkGWLQ',
                                description:
                                  'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
                              },
                              {
                                daily,
                              },
                            )}
                          </div>
                        )}
                        {upcoming !== null && (
                          <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                            {intl.formatMessage(
                              {
                                defaultMessage: '同時に持てる予約 {upcoming}回',
                                id: 'm0xxG1',
                                description:
                                  'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
                              },
                              { upcoming },
                            )}
                          </div>
                        )}
                      </>
                    }
                  />
                );
              })()}
              <RSDescriptionListItem
                term={intl.formatMessage({
                  defaultMessage: 'お支払い方法',
                  id: 'bzR6JD',
                  description:
                    'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
                })}
                description={
                  <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                    {intl.formatMessage({
                      defaultMessage: 'クレジットカード決済',
                      id: 'BLEnKC',
                      description:
                        'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
                    })}
                  </div>
                }
              />
            </RSDescriptionList>
          </div>

          <div className="mb-4">
            <p className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
              {intl.formatMessage(
                {
                  defaultMessage:
                    '購入完了すると、{terms}に同意したとみなします。キャンセルその他の販売条件は、{sctl}をあわせてご確認ください。',
                  id: 'Gb+czB',
                  description:
                    'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
                },
                {
                  terms: (
                    <RSAnchor
                      href="https://stores.fun/legal/reserve-user-terms"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {intl.formatMessage({
                        defaultMessage: '利用規約',
                        id: 'AQlCYb',
                        description:
                          'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
                      })}
                    </RSAnchor>
                  ),
                  sctl: (
                    <RSAnchor
                      href={buildSiteUrl(`/legal_notice`)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {intl.formatMessage({
                        defaultMessage: '特定商取引法に基づく表示',
                        id: 'qX5mzE',
                        description:
                          'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
                      })}
                    </RSAnchor>
                  ),
                },
              )}
            </p>
          </div>

          <div>
            <RSButton
              variant="large"
              isBlock
              onClick={onSubmitButtonClick}
              disabled={state.value['confirming'] === 'subscriptionSubmitting'}
              isLoading={state.value['confirming'] === 'subscriptionSubmitting'}
            >
              {intl.formatMessage({
                defaultMessage: '購入確定',
                id: 'bQEHUi',
                description:
                  'src/organisms/product-purchase/SubscriptionPurchaseModal/components/ConfirmView/index.tsx',
              })}
            </RSButton>
          </div>
        </div>
      </div>
    </>
  );
};
